import { useEffect, useState } from "react";
import axios from "axios";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CreditCard, Plus, Trash2, Package, Loader2 } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Conversion rate: 1 USD = 148 HTG
const USD_TO_HTG_RATE = 148;

export const AdminCards = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Single card form
  const [singleForm, setSingleForm] = useState({
    code: "",
    denomination: "",
    price_htg: ""
  });

  // Bulk form
  const [bulkForm, setBulkForm] = useState({
    denomination: "",
    price_htg: "",
    count: 5
  });

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const response = await axios.get(`${API}/gift-cards/all`);
      setCards(response.data);
    } catch (error) {
      console.error("Error fetching cards:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDenominationChange = (value, formType) => {
    const denom = parseInt(value) || 0;
    const calculatedPrice = denom * USD_TO_HTG_RATE;
    
    if (formType === 'single') {
      setSingleForm({
        ...singleForm,
        denomination: value,
        price_htg: calculatedPrice
      });
    } else {
      setBulkForm({
        ...bulkForm,
        denomination: value,
        price_htg: calculatedPrice
      });
    }
  };

  const handleAddSingleCard = async (e) => {
    e.preventDefault();
    if (!singleForm.code) {
      toast.error("Tanpri antre kòd kat la");
      return;
    }
    if (!singleForm.denomination || parseInt(singleForm.denomination) < 1) {
      toast.error("Tanpri antre yon montan valid");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/gift-cards`, {
        code: singleForm.code,
        denomination: parseInt(singleForm.denomination),
        price_htg: singleForm.price_htg
      });
      toast.success("Kat kado ajoute!");
      setAddDialogOpen(false);
      setSingleForm({ code: "", denomination: "", price_htg: "" });
      fetchCards();
    } catch (error) {
      console.error("Error adding card:", error);
      toast.error("Erè pandan ajoute kat la");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddBulkCards = async (e) => {
    e.preventDefault();
    if (!bulkForm.denomination || parseInt(bulkForm.denomination) < 1) {
      toast.error("Tanpri antre yon montan valid");
      return;
    }
    if (bulkForm.count < 1 || bulkForm.count > 100) {
      toast.error("Kantite dwe ant 1 ak 100");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/gift-cards/bulk`, {
        denomination: parseInt(bulkForm.denomination),
        price_htg: bulkForm.price_htg,
        count: bulkForm.count
      });
      toast.success(`${bulkForm.count} kat kado ajoute!`);
      setBulkDialogOpen(false);
      setBulkForm({ denomination: "", price_htg: "", count: 5 });
      fetchCards();
    } catch (error) {
      console.error("Error adding bulk cards:", error);
      toast.error("Erè pandan ajoute kat yo");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm("Èske ou sèten ou vle efase kat sa a?")) return;

    try {
      await axios.delete(`${API}/gift-cards/${cardId}`);
      toast.success("Kat efase!");
      fetchCards();
    } catch (error) {
      console.error("Error deleting card:", error);
      toast.error(error.response?.data?.detail || "Erè pandan efase kat la");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-HT', {
      style: 'decimal',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-HT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Count cards by status
  const availableCards = cards.filter(c => !c.is_sold);
  const soldCards = cards.filter(c => c.is_sold);

  return (
    <AdminLayout>
      <div data-testid="admin-cards">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <h1 className="text-3xl font-bold text-[#232F3E]">Kat Kado</h1>
          
          <div className="flex gap-2">
            {/* Add Single Card Dialog */}
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#FF9900] hover:bg-[#F08C00] text-black" data-testid="add-single-card-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajoute Kat
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ajoute Yon Kat Kado</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddSingleCard} className="space-y-4" data-testid="add-single-form">
                  <div className="space-y-2">
                    <Label>Kòd Kat Kado</Label>
                    <Input
                      placeholder="AMZN-XXXX-XXXX-XXXX"
                      value={singleForm.code}
                      onChange={(e) => setSingleForm({...singleForm, code: e.target.value.toUpperCase()})}
                      data-testid="card-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Montan (USD)</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="Antre montan (egz: 10, 25, 50, 100, 75...)"
                      value={singleForm.denomination}
                      onChange={(e) => handleDenominationChange(e.target.value, 'single')}
                      data-testid="denomination-input"
                    />
                    <p className="text-xs text-gray-500">Ou ka antre nenpòt montan ou vle</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Pri (HTG)</Label>
                    <Input
                      type="number"
                      value={singleForm.price_htg}
                      onChange={(e) => setSingleForm({...singleForm, price_htg: parseInt(e.target.value) || 0})}
                      data-testid="price-input"
                    />
                    <p className="text-xs text-gray-500">Tò otomatik: 1 USD = {USD_TO_HTG_RATE} HTG (ou ka ajiste)</p>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#FF9900] hover:bg-[#F08C00] text-black"
                    disabled={submitting}
                    data-testid="submit-single-card"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Ajoute
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Bulk Add Dialog */}
            <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-bulk-cards-btn">
                  <Package className="w-4 h-4 mr-2" />
                  Ajoute an Gwo
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ajoute Plizyè Kat (Kòd Otomatik)</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddBulkCards} className="space-y-4" data-testid="add-bulk-form">
                  <div className="space-y-2">
                    <Label>Montan (USD)</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="Antre montan (egz: 10, 25, 50, 100, 75...)"
                      value={bulkForm.denomination}
                      onChange={(e) => handleDenominationChange(e.target.value, 'bulk')}
                      data-testid="bulk-denomination-input"
                    />
                    <p className="text-xs text-gray-500">Ou ka antre nenpòt montan ou vle</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Pri (HTG)</Label>
                    <Input
                      type="number"
                      value={bulkForm.price_htg}
                      onChange={(e) => setBulkForm({...bulkForm, price_htg: parseInt(e.target.value) || 0})}
                      data-testid="bulk-price-input"
                    />
                    <p className="text-xs text-gray-500">Tò otomatik: 1 USD = {USD_TO_HTG_RATE} HTG (ou ka ajiste)</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Kantite Kat</Label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={bulkForm.count}
                      onChange={(e) => setBulkForm({...bulkForm, count: parseInt(e.target.value) || 1})}
                      data-testid="bulk-count-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#FF9900] hover:bg-[#F08C00] text-black"
                    disabled={submitting}
                    data-testid="submit-bulk-cards"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Jenere {bulkForm.count} Kat
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Inventory Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <Card className="border-0 shadow-lg bg-green-50" data-testid="available-summary">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-green-700">Kat Disponib</p>
                  <p className="text-2xl font-bold text-green-800">{availableCards.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg bg-purple-50" data-testid="sold-summary">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-purple-700">Kat Vann</p>
                  <p className="text-2xl font-bold text-purple-800">{soldCards.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="loading-spinner" data-testid="loading-spinner"></div>
          </div>
        ) : (
          <Card className="border-0 shadow-lg" data-testid="cards-table-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#FF9900]" />
                Tout Kat ({cards.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cards.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full admin-table">
                    <thead>
                      <tr className="border-b">
                        <th className="py-3 px-4">Kòd</th>
                        <th className="py-3 px-4">Valè</th>
                        <th className="py-3 px-4">Pri</th>
                        <th className="py-3 px-4">Estati</th>
                        <th className="py-3 px-4">Dat Kreye</th>
                        <th className="py-3 px-4">Aksyon</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cards.map((card) => (
                        <tr key={card.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-mono text-sm">{card.code}</td>
                          <td className="py-3 px-4 font-bold">${card.denomination}</td>
                          <td className="py-3 px-4">{formatPrice(card.price_htg)} HTG</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              card.is_sold 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {card.is_sold ? 'Vann' : 'Disponib'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            {formatDate(card.created_at)}
                          </td>
                          <td className="py-3 px-4">
                            {!card.is_sold && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteCard(card.id)}
                                data-testid={`delete-card-${card.id.slice(0, 8)}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Pa gen kat kado ankò</p>
                  <p className="text-sm">Klike "Ajoute Kat" pou ajoute premye kat ou</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

